import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useFullBleedPane } from '@/hooks/useFullBleedPane';

/**
 * One layout for every scheduling page.
 *
 * The shared dashboard shell pours each page into a centred 1280px column with
 * vertical padding. That is right for a settings form and wrong for eight
 * courts across two days: on a 1851px monitor it threw away 315px of width and
 * squeezed the board's height. This shell hands the whole right-hand pane to
 * the page — fluid width, no fixed heights — with exactly one header line, an
 * optional control row, and one scrolling content area.
 *
 * Full screen hides the sidebar and the app header; Esc comes back.
 */

const FULLSCREEN_ATTR = 'data-scheduling-fullscreen';

interface Props {
  /** The page's single heading. */
  title: React.ReactNode;
  /** Small line under the heading (event name, draft generated…). */
  subtitle?: React.ReactNode;
  /** This page's actions, rendered on the right of the header line. */
  actions?: React.ReactNode;
  /** One optional control row under the header. */
  controlRow?: React.ReactNode;
  /**
   * true  → the child owns the scrolling (the board, the bracket canvas).
   * false → this shell is the page's single scroller.
   */
  fill?: boolean;
  children: React.ReactNode;
}

export function SchedulingPageShell({
  title,
  subtitle,
  actions,
  controlRow,
  fill = false,
  children,
}: Props) {
  const [fullscreen, setFullscreen] = React.useState(false);

  // The pane is the window minus the app header. No pixel heights anywhere.
  useFullBleedPane(true);

  React.useEffect(() => {
    const body = document.body;
    if (fullscreen) body.setAttribute(FULLSCREEN_ATTR, 'true');
    else body.removeAttribute(FULLSCREEN_ATTR);
    return () => body.removeAttribute(FULLSCREEN_ATTR);
  }, [fullscreen]);

  React.useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const fullscreenButton = (
    <Button
      size="sm"
      onClick={() => setFullscreen((v) => !v)}
      className="bg-primary text-primary-foreground hover:bg-primary/90"
      title={fullscreen ? 'Exit full screen (Esc)' : 'Fill the whole window'}
    >
      {fullscreen ? (
        <><Minimize2 className="mr-1.5 h-4 w-4" /> Exit full screen</>
      ) : (
        <><Maximize2 className="mr-1.5 h-4 w-4" /> Full screen</>
      )}
    </Button>
  );

  return (
    <div data-scheduling-page className="relative flex min-h-0 w-full flex-1 flex-col">
      {!fullscreen && (
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold tracking-tight">{title}</h1>
            {subtitle && (
              <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {actions}
            {fullscreenButton}
          </div>
        </div>
      )}

      {fullscreen && (
        <div className="pointer-events-none absolute right-3 top-3 z-50">
          <span className="pointer-events-auto">{fullscreenButton}</span>
        </div>
      )}

      {controlRow && (
        <div className="shrink-0 border-b px-4 py-2 sm:px-6">{controlRow}</div>
      )}

      <div
        className={
          fill
            ? 'flex min-h-0 w-full flex-1 flex-col overflow-hidden'
            : 'min-h-0 w-full flex-1 overflow-auto px-4 py-4 sm:px-6'
        }
      >
        {children}
      </div>
    </div>
  );
}

export default SchedulingPageShell;
